import type { QueryClient } from "@tanstack/react-query"
import { noop } from "@tanstack/react-query"
import type { Friend } from "@/entities/friend"
import { wsBridge, wsStore, wsUserId } from "@/shared/api/websocket"
import { syncFriendScenarios } from "../lib/sync-friend-scenarios"
import { scenarioKeys } from "../model/keys"
import { primePendingScenarios, scenarioQueryOptions } from "./queries"

// Both scenario events carry the same shape; only `proposed` prefetches the
// text — the popup it triggers should render without a request.
function handleScenarioEvent(
	payload: {
		friend_id: number
		scenarios: Friend["scenarios"]
	},
	queryClient: QueryClient,
	prefetchScenario: boolean,
) {
	const userId = wsUserId()
	if (!userId) return

	syncFriendScenarios(queryClient, userId, payload.friend_id, payload.scenarios)
	// One forced read instead of invalidate + prefetch: with an active
	// observer those two fire the same request twice.
	if (prefetchScenario) {
		void queryClient
			.query({
				...scenarioQueryOptions(payload.friend_id),
				staleTime: 0,
			})
			.catch(noop)
		return
	}

	queryClient.invalidateQueries({
		queryKey: scenarioKeys.current(payload.friend_id),
	})
}

export function registerScenarioEvents(): void {
	wsStore.on("state", ({ isConnected }) => {
		if (!isConnected) return
		const { queryClient } = wsBridge.getContext()
		const userId = wsUserId()
		if (!queryClient || !userId) return
		void primePendingScenarios(queryClient, userId)
	})

	wsBridge.subscribe("scenario.proposed", (payload, queryClient) =>
		handleScenarioEvent(payload, queryClient, true),
	)

	wsBridge.subscribe("scenario.accepted", (payload, queryClient) =>
		handleScenarioEvent(payload, queryClient, false),
	)
}
